import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/lib/Buttons';
import { BaseModal } from '@/lib/Modals';
import { TabItem, Tabs } from '@/lib/Tabs';
import { MarketIntelligenceSetup } from './components/MarketIntelligenceSetup';
import { MarketIntelligenceReport } from './components/MarketIntelligenceReport';
import { MarketIntelligenceDataProfile } from './components/MarketIntelligenceDataProfile';
import { useMarketIntelligenceReportFlow } from './hooks/useMarketIntelligenceReportFlow';
import { MarketIntelligenceTab } from './types';
import { PageHeader, PageLayout } from '@/global/components';
import RouteConstants from '@/features/router/RouteConstants';
import { BreadcrumbItem } from '@/lib/Breadcrumbs';

const breadcrumbs: BreadcrumbItem[] = [
  {
    label: 'ALL.marketIntelligence',
    path: RouteConstants.marketIntellingence,
  },
];

const MARKET_INTELLIGENCE_TABS: Record<MarketIntelligenceTab, TabItem['title']> = {
  setup: 'marketIntelligence.tabs.setup',
  report: 'marketIntelligence.tabs.report',
  dataProfile: 'marketIntelligence.tabs.dataProfile',
};

const renderStepPrefix = (step: number) => (isSelected: boolean) => {
  const className = isSelected
    ? 'flex size-6 items-center justify-center rounded-full bg-brand-600 text-sm font-semibold text-white-100'
    : 'flex size-6 items-center justify-center rounded-full bg-primary-200 text-sm font-semibold text-primary-700';

  return <span className={className}>{step}</span>;
};

export const MarketIntelligence: FC = () => {
  const { t } = useTranslation('marketIntelligence');
  const {
    activeTab,
    generateReport,
    handleCloseReportErrorModal,
    handleRegenerateReport,
    handleReportError,
    handleReportReady,
    handleRetryReport,
    isGenerating,
    isReportHistoryLoading,
    isReportPending,
    isRetryingReport,
    latestReportGenerationFailed,
    maxReportRetryCount,
    report,
    reportErrorModal,
    reportId,
    setActiveTab,
  } = useMarketIntelligenceReportFlow();

  const titlePrefix = t('page.titlePrefix');
  const tabs: TabItem[] = [
    {
      title: MARKET_INTELLIGENCE_TABS.setup,
      prefix: renderStepPrefix(1),
      element: <MarketIntelligenceSetup isGenerating={isGenerating} onGenerateReport={generateReport} />,
    },
    {
      title: MARKET_INTELLIGENCE_TABS.report,
      prefix: renderStepPrefix(2),
      element: (
        <MarketIntelligenceReport
          report={report}
          reportId={reportId}
          isGenerating={isGenerating || isReportPending}
          isLoadingReportHistory={isReportHistoryLoading}
          showLatestReportWarning={latestReportGenerationFailed}
          onReportReady={handleReportReady}
          onReportError={handleReportError}
          onRegenerate={handleRegenerateReport}
        />
      ),
    },
    {
      title: MARKET_INTELLIGENCE_TABS.dataProfile,
      prefix: renderStepPrefix(3),
      element: <MarketIntelligenceDataProfile />,
    },
  ];

  const handleSelectTab = (tab: TabItem) => {
    const nextTab = Object.entries(MARKET_INTELLIGENCE_TABS).find(([, title]) => title === tab.title)?.[0] as MarketIntelligenceTab | undefined;

    if (nextTab) {
      setActiveTab(nextTab);
    }
  };

  return (
    <PageLayout>
      <PageHeader breadcrumbs={breadcrumbs} title='ALL.marketIntelligence' subtitle='marketIntelligence.page.subtitle' />
      <div className='pb-4'>
        {/* Tabs */}
        <Tabs
          items={tabs}
          currentTab={MARKET_INTELLIGENCE_TABS[activeTab]}
          onSelectTab={handleSelectTab}
          title={titlePrefix}
          classNames={{ wrapper: 'gap-6', nav: 'border-primary-border', tabWrapper: 'flex-row gap-6' }}
        />
      </div>

      {reportErrorModal ? (
        <BaseModal onClose={handleCloseReportErrorModal} isDismissable={!isRetryingReport}>
          <div>
            <h2 className='text-lg font-semibold text-primary-900'>
              {reportErrorModal.errorKind === 'TRANSIENT' ? t('generating.errors.transientTitle') : t('generating.errors.title')}
            </h2>
            <p className='mt-2 text-sm leading-5 text-primary-600'>{reportErrorModal.errorMessage}</p>
            {reportErrorModal.errorKind === 'TRANSIENT' && reportErrorModal.retryCount >= maxReportRetryCount ? (
              <p className='mt-3 text-sm leading-5 text-primary-600'>{t('generating.errors.retryLimitReached')}</p>
            ) : null}
          </div>

          {reportErrorModal.errorKind === 'TRANSIENT' && reportErrorModal.retryCount < maxReportRetryCount ? (
            <div className='grid grid-cols-1 gap-3 pt-4 sm:grid-cols-2'>
              <Button
                intent='secondary'
                size='lg'
                text={t('generating.errors.backToSetup')}
                onClick={handleCloseReportErrorModal}
                disabled={isRetryingReport}
              />
              <Button size='lg' text={t('generating.errors.retry')} onClick={handleRetryReport} isLoading={isRetryingReport} />
            </div>
          ) : (
            <div className='pt-4'>
              <Button size='lg' text={t('generating.errors.confirm')} onClick={handleCloseReportErrorModal} disabled={isRetryingReport} />
            </div>
          )}
        </BaseModal>
      ) : null}
    </PageLayout>
  );
};
